/*
  RFC 9562 UUID version 7: 48 bits of Unix milliseconds, then version, then 74 random
  bits. Made on the device for every command and every record created offline (brief §5,
  ADR 0002), so keys sort by creation time and the outbox posts in order by key alone.

  WHY NOT crypto.randomUUID()

  That is a v4: random, so a SQL Server clustered or unique index on it fragments, and
  the outbox would need a second sequence column to post in order.

  Monotonic within a millisecond: the first 12 random bits (rand_a) are a counter seeded
  randomly per millisecond and incremented per call, so two commands captured in one
  tick still sort in capture order. If the counter overflows the clock is nudged forward
  one millisecond, as §6.2 method 1 allows.
*/

let lastMs = -1
let counter = 0

export function uuidv7(now: () => number = Date.now): string {
  let ms = now()
  if (ms <= lastMs) {
    // Same tick, or a clock that went backwards: stay monotonic.
    ms = lastMs
    counter += 1
    if (counter > 0xfff) {
      ms = lastMs + 1
      counter = randomCounter()
    }
  } else {
    counter = randomCounter()
  }
  lastMs = ms

  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)

  // 48-bit timestamp, big-endian.
  bytes[0] = (ms / 2 ** 40) & 0xff
  bytes[1] = (ms / 2 ** 32) & 0xff
  bytes[2] = (ms / 2 ** 24) & 0xff
  bytes[3] = (ms / 2 ** 16) & 0xff
  bytes[4] = (ms / 2 ** 8) & 0xff
  bytes[5] = ms & 0xff
  // Version 7 in the high nibble, 12-bit counter in rand_a.
  bytes[6] = 0x70 | ((counter >> 8) & 0x0f)
  bytes[7] = counter & 0xff
  // RFC 4122 variant (10xx) in the high bits of byte 8.
  bytes[8] = (bytes[8]! & 0x3f) | 0x80

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

function randomCounter(): number {
  const seed = new Uint16Array(1)
  crypto.getRandomValues(seed)
  // Leave headroom so the counter rarely overflows within one millisecond.
  return seed[0]! & 0x7ff
}

/** The millisecond timestamp embedded in a v7 id. */
export function uuidv7Time(id: string): number {
  const hex = id.replaceAll('-', '').slice(0, 12)
  return parseInt(hex, 16)
}

export const UUID_V7 = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/

/** Test seam. */
export function resetUuidv7ForTests(): void {
  lastMs = -1
  counter = 0
}
